import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { createAppSlice } from '.';
import { SNACK_CONSTANTS } from '../constants/snackConstants';
import { I18n, LocaleKey } from '../i18n';
import { Values } from '../utils/types';

type SnackId = string;

type SnackDispatch = ThunkDispatch<unknown, unknown, UnknownAction>;

type SnackType = Values<typeof SNACK_CONSTANTS.TYPE>;
type SnackAction = Values<typeof SNACK_CONSTANTS.ACTION>;
type Snack = {
  message: string;
  duration?: number;
  type?: SnackType;
  action?: SnackAction;
};

type SnackWithId = {
  id: SnackId;
} & Snack;

type State = {
  byId: Record<SnackId, SnackWithId>;
  queue: SnackId[];
};

const initialState: State = {
  byId: {},
  queue: [],
};

function addIdToSnack(snack: Snack): { payload: SnackWithId } {
  return { payload: { id: crypto.randomUUID(), ...snack } };
}

const snacksSlice = createAppSlice({
  name: 'snacks',
  initialState,
  reducers: (create) => ({
    snackAdded: create.preparedReducer(addIdToSnack, (state, { payload }) => {
      state.byId[payload.id] = payload;
      state.queue.push(payload.id);
    }),
    snackDismissed: create.reducer<SnackId>((state, { payload }) => {
      state.queue = state.queue.filter((id) => id !== payload);
    }),
  }),
  selectors: {
    selectSnacksById: (state) => state.byId,
    selectSnackQueue: (state) => state.queue,
  },
});

export const { snackAdded, snackDismissed } = snacksSlice.actions;
export const { selectSnacksById, selectSnackQueue } = snacksSlice.selectors;

export function success(
  messageKey: LocaleKey,
  options: {
    duration?: number;
    action?: SnackAction;
    params?: Parameters<typeof I18n.t>[1];
    dispatch: SnackDispatch;
  },
) {
  options.dispatch(
    snackAdded({
      type: SNACK_CONSTANTS.TYPE.SUCCESS,
      duration: options.duration || 3000,
      message: I18n.t(messageKey, options.params),
      action: options.action || SNACK_CONSTANTS.ACTION.OK,
    }),
  );
}

export function failure(
  messageKey: LocaleKey,
  options: {
    duration?: number;
    action?: SnackAction;
    params?: Parameters<typeof I18n.t>[1];
    dispatch: SnackDispatch;
  },
) {
  options.dispatch(
    snackAdded({
      type: SNACK_CONSTANTS.TYPE.ERROR,
      duration: options.duration || 5000,
      message: I18n.t(messageKey, options.params),
      action: options.action || SNACK_CONSTANTS.ACTION.OK,
    }),
  );
}

export function errorHasOccurred(e: unknown, dispatch: SnackDispatch): never {
  if (import.meta.env.MODE === 'test') {
    // the e2e suite reads this attribute to tell that a call failed
    document.body.setAttribute(
      'data-snack-error',
      typeof e === 'object' && e !== null && 'errors' in e ? JSON.stringify(e) : String(e),
    );
  }
  failure('Api.failedCall', { dispatch });
  throw e;
}

export async function alerting<T = unknown>(
  call: () => Promise<T>,
  options: {
    dispatch: SnackDispatch;
  },
): Promise<T> {
  try {
    return await call();
  } catch (e) {
    return errorHasOccurred(e, options.dispatch);
  }
}

export default snacksSlice.reducer;
